import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileCheck, CreditCard, CheckCircle2, Pencil, Mail, MessageCircle } from "lucide-react";
import {
  getQuote, getInvoice, getSettings, convertQuote,
  setInvoiceStatus, createCheckout, PDF_URL,
} from "@/lib/api";
import { fmtEur, fmtDate, fmtNum } from "@/lib/format";
import { useI18n } from "@/i18n";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const DocumentView = ({ kind }) => {
  const { t, lang } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const isQuote = kind === "quote";
  const base = isQuote ? "quotes" : "invoices";
  const [doc, setDoc] = useState(null);
  const [settings, setSettings] = useState({});
  const [paying, setPaying] = useState(false);

  const load = () => (isQuote ? getQuote(id) : getInvoice(id)).then(setDoc);
  useEffect(() => { load(); getSettings().then(setSettings); }, [id, kind]);

  if (!doc) return <div className="text-muted-foreground">{t("common.loading")}</div>;

  const convert = async () => {
    const inv = await convertQuote(doc.id);
    toast.success(t("doc.converted"));
    navigate(`/invoices/${inv.id}`);
  };

  const markPaid = async () => { await setInvoiceStatus(doc.id, "paid"); toast.success(t("status.paid")); load(); };

  const pay = async () => {
    setPaying(true);
    try {
      const { checkout_url } = await createCheckout(doc.id);
      window.location.href = checkout_url;
    } catch {
      toast.error("Error");
      setPaying(false);
    }
  };

  const L = {
    de: { billTo: "Rechnung an", number: "Nr.", date: "Datum", due: "Fällig am", service: "Leistungsdatum", valid: "Gültig bis", desc: "Beschreibung", qty: "Menge", price: "Einzelpreis", tax: "MwSt.", amount: "Betrag" },
    fr: { billTo: "Facturé à", number: "N°", date: "Date", due: "Échéance", service: "Date de prestation", valid: "Valable jusqu'au", desc: "Description", qty: "Qté", price: "Prix unit.", tax: "TVA", amount: "Montant" },
    en: { billTo: "Bill to", number: "No.", date: "Date", due: "Due date", service: "Service date", valid: "Valid until", desc: "Description", qty: "Qty", price: "Unit price", tax: "VAT", amount: "Amount" },
  }[lang];
  const docTitle = { de: isQuote ? "ANGEBOT" : "RECHNUNG", fr: isQuote ? "DEVIS" : "FACTURE", en: isQuote ? "QUOTE" : "INVOICE" }[lang];

  const pdfLink = PDF_URL(kind, doc.id, lang);
  const shareText = `${t("doc.shareHello")}\n\n${docTitle} ${doc.number}\n${doc.customer?.name} · ${fmtEur(doc.total)}\n\n${pdfLink}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const mailHref = `mailto:${doc.customer?.email || ""}?subject=${encodeURIComponent(`${docTitle} ${doc.number}`)}&body=${encodeURIComponent(shareText)}`;

  return (
    <div className="mx-auto max-w-4xl">
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => navigate(`/${base}`)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-list">
          <ArrowLeft className="h-4 w-4" /> {t("common.back")}
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(`/${base}/${doc.id}/edit`)} data-testid="edit-document-button"><Pencil className="mr-1.5 h-4 w-4" /> {t("common.edit")}</Button>
          <a href={PDF_URL(kind, doc.id, lang)} target="_blank" rel="noreferrer" data-testid="download-pdf-link">
            <Button variant="outline" size="sm"><Download className="mr-1.5 h-4 w-4" /> {t("common.downloadPdf")}</Button>
          </a>
          <a href={waHref} target="_blank" rel="noreferrer" data-testid="share-whatsapp-link">
            <Button variant="outline" size="sm" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"><MessageCircle className="mr-1.5 h-4 w-4" /> {t("doc.whatsapp")}</Button>
          </a>
          <a href={mailHref} data-testid="share-email-link">
            <Button variant="outline" size="sm"><Mail className="mr-1.5 h-4 w-4" /> {t("doc.email")}</Button>
          </a>
          {isQuote && doc.status !== "accepted" && (
            <Button size="sm" onClick={convert} data-testid="convert-quote-button"><FileCheck className="mr-1.5 h-4 w-4" /> {t("doc.convert")}</Button>
          )}
          {!isQuote && doc.status !== "paid" && (
            <>
              <Button variant="outline" size="sm" onClick={markPaid} data-testid="mark-paid-button"><CheckCircle2 className="mr-1.5 h-4 w-4" /> {t("doc.markPaid")}</Button>
              <Button size="sm" onClick={pay} disabled={paying} data-testid="pay-invoice-button"><CreditCard className="mr-1.5 h-4 w-4" /> {t("doc.pay")}</Button>
            </>
          )}
        </div>
      </div>

      {/* A4 document */}
      <div className="print-area mx-auto rounded-sm border border-border bg-white p-8 shadow-md sm:p-12" data-testid="document-preview">
        <div className="flex items-start justify-between">
          <div>
            {settings.logo && (
              <img src={settings.logo} alt="logo" className="mb-3 max-h-16 max-w-[180px] object-contain" data-testid="document-logo" />
            )}
            <p className="font-display text-xl font-bold text-slate-900">{settings.company_name}</p>
            <p className="mt-1 max-w-xs text-xs leading-relaxed text-muted-foreground">
              {[settings.address, [settings.postal_code, settings.city].filter(Boolean).join(" "), settings.country].filter(Boolean).join(" · ")}
            </p>
            {settings.email && <p className="text-xs text-muted-foreground">{settings.email}</p>}
          </div>
          <div className="text-right">
            <p className="font-display text-2xl font-bold tracking-tight text-primary">{docTitle}</p>
            <div className="mt-2"><StatusBadge status={doc.status} /></div>
          </div>
        </div>

        <div className="my-8 h-px bg-border" />

        <div className="flex justify-between gap-8">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{L.billTo}</p>
            <p className="mt-1.5 font-semibold text-slate-900">{doc.customer?.name}</p>
            <p className="text-sm text-slate-600">{doc.customer?.address}</p>
            <p className="text-sm text-slate-600">{[doc.customer?.postal_code, doc.customer?.city].filter(Boolean).join(" ")}</p>
            <p className="text-sm text-slate-600">{doc.customer?.country}</p>
          </div>
          <div className="space-y-1 text-right text-sm">
            <div className="flex justify-between gap-6"><span className="text-muted-foreground">{L.number}</span><span className="font-mono-num font-semibold text-slate-900">{doc.number}</span></div>
            <div className="flex justify-between gap-6"><span className="text-muted-foreground">{L.date}</span><span className="font-mono-num">{fmtDate(doc.issue_date)}</span></div>
            {isQuote && doc.valid_until && <div className="flex justify-between gap-6"><span className="text-muted-foreground">{L.valid}</span><span className="font-mono-num">{fmtDate(doc.valid_until)}</span></div>}
            {!isQuote && doc.service_date && <div className="flex justify-between gap-6"><span className="text-muted-foreground">{L.service}</span><span className="font-mono-num">{fmtDate(doc.service_date)}</span></div>}
            {!isQuote && doc.due_date && <div className="flex justify-between gap-6"><span className="text-muted-foreground">{L.due}</span><span className="font-mono-num">{fmtDate(doc.due_date)}</span></div>}
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary text-left text-xs font-semibold uppercase tracking-wide text-primary-foreground">
                <th className="px-4 py-3">{L.desc}</th>
                <th className="px-4 py-3 text-right">{L.qty}</th>
                <th className="px-4 py-3 text-right">{L.price}</th>
                <th className="px-4 py-3 text-right">{L.tax}</th>
                <th className="px-4 py-3 text-right">{L.amount}</th>
              </tr>
            </thead>
            <tbody>
              {doc.items?.map((it, i) => (
                <tr key={i} className="border-t border-border even:bg-slate-50">
                  <td className="px-4 py-3 text-slate-800">{it.description}</td>
                  <td className="px-4 py-3 text-right font-mono-num text-slate-600">{fmtNum(it.quantity)}</td>
                  <td className="px-4 py-3 text-right font-mono-num text-slate-600">{fmtEur(it.unit_price)}</td>
                  <td className="px-4 py-3 text-right font-mono-num text-slate-600">{fmtNum(it.tax_rate)}%</td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold text-slate-900">{fmtEur(it.line_total ?? it.quantity * it.unit_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end">
          <div className="w-64 space-y-2 text-sm">
            <div className="flex justify-between text-slate-600"><span>{t("doc.subtotal")}</span><span className="font-mono-num">{fmtEur(doc.subtotal)}</span></div>
            <div className="flex justify-between text-slate-600"><span>{t("doc.tax")}</span><span className="font-mono-num">{fmtEur(doc.tax_total)}</span></div>
            <div className="flex justify-between border-t-2 border-slate-900 pt-2 text-lg font-bold text-slate-900"><span>{t("common.total")}</span><span className="font-mono-num text-primary">{fmtEur(doc.total)}</span></div>
          </div>
        </div>

        {doc.notes && (
          <div className="mt-8">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{t("doc.notes")}</p>
            <p className="mt-1.5 text-sm text-slate-600">{doc.notes}</p>
          </div>
        )}

        {doc.payment_terms && (
          <div className="mt-6">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{t("doc.paymentTerms")}</p>
            <p className="mt-1.5 text-sm text-slate-600">{doc.payment_terms}</p>
          </div>
        )}

        {(settings.iban || settings.tax_id) && (
          <div className="mt-10 border-t border-border pt-4 text-xs text-muted-foreground">
            {settings.iban && <p>{settings.bank_name} · IBAN: {settings.iban}</p>}
            {settings.tax_id && <p>{L.tax} ID: {settings.tax_id}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentView;
